<template>
  <div class="invoices wf-page wf-results-page">
    <div class="main-wrapper">
      <layout-header></layout-header>
      <layout-sidebar></layout-sidebar>

      <div class="page-wrapper">
        <div class="content container-fluid">
          <div class="page-header">
            <div class="row align-items-center">
              <div class="col">
                <h3 class="page-title">Hasil Pengisian</h3>
                <p class="text-muted mb-2">
                  {{ formName || "Lihat respons dan performa formulir Anda." }}
                </p>
                <ul class="breadcrumb">
                  <li class="breadcrumb-item">
                    <router-link to="/index">Dashboard</router-link>
                  </li>
                  <li class="breadcrumb-item">
                    <router-link to="/forms">Form Builder</router-link>
                  </li>
                  <li class="breadcrumb-item active">Hasil Pengisian</li>
                </ul>
              </div>
              <div class="col-auto float-right ml-auto wf-results-actions">
                <router-link
                  v-if="!pageError && formId"
                  :to="editRoute"
                  class="btn btn-outline-secondary btn-rounded"
                >
                  <i class="fa fa-pencil" aria-hidden="true"></i>
                  Kelola Form
                </router-link>
              </div>
            </div>
          </div>

          <div v-if="formLoading" class="card wf-results-card wf-state-card">
            <i class="fa fa-spinner fa-spin wf-state-icon" aria-hidden="true"></i>
            <h5>Memuat hasil pengisian</h5>
            <p class="text-muted mb-0">Formulir dan respons sedang disiapkan.</p>
          </div>

          <div v-else-if="pageError" class="card wf-results-card wf-state-card">
            <div class="wf-state-icon is-error">
              <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
            </div>
            <h5>Hasil pengisian belum dapat ditampilkan</h5>
            <p class="text-muted">{{ pageErrorMessage }}</p>
            <div class="wf-state-actions">
              <button type="button" class="btn wf-primary-btn" @click="loadPage">
                <i class="fa fa-refresh" aria-hidden="true"></i>
                Coba Lagi
              </button>
              <router-link to="/forms" class="btn btn-outline-secondary">
                Kembali ke Form Builder
              </router-link>
            </div>
          </div>

          <template v-else>
            <div class="row wf-stat-row">
              <div
                v-for="card in statCards"
                :key="card.key"
                class="col-xl-3 col-md-6"
              >
                <div class="card wf-stat-card">
                  <div class="wf-stat-icon" :class="'is-' + card.tone">
                    <i :class="card.icon" aria-hidden="true"></i>
                  </div>
                  <div class="wf-stat-copy">
                    <span class="wf-stat-label">{{ card.label }}</span>
                    <strong class="wf-stat-value">{{ card.value }}</strong>
                    <small v-if="card.hint">{{ card.hint }}</small>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="statsError" class="alert alert-warning wf-alert" role="alert">
              <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
              <span>{{ statsErrorMessage }}</span>
            </div>

            <div class="card wf-results-card">
              <div class="card-header wf-card-header">
                <div>
                  <h4 class="card-title mb-1">Tren 7 Hari Terakhir</h4>
                  <small class="text-muted">
                    Jumlah respons per hari berdasarkan data backend.
                  </small>
                </div>
                <span v-if="stats && stats.timezone" class="wf-timezone-chip">
                  <i class="fa fa-clock-o" aria-hidden="true"></i>
                  {{ stats.timezone }}
                </span>
              </div>
              <div class="card-body">
                <div v-if="statsLoading" class="wf-inline-loading">
                  <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                  Menghitung statistik...
                </div>
                <div v-else-if="!stats" class="wf-inline-empty">
                  Statistik belum tersedia.
                </div>
                <div v-else-if="!trendHasData" class="wf-inline-empty">
                  Belum ada respons pada 7 hari terakhir.
                </div>
                <div
                  v-else
                  class="wf-trend-chart"
                  role="img"
                  :aria-label="trendAriaLabel"
                >
                  <div
                    v-for="point in trendBars"
                    :key="point.date"
                    class="wf-trend-column"
                    :title="point.title"
                  >
                    <span class="wf-trend-count">{{ point.count }}</span>
                    <div class="wf-trend-track">
                      <div
                        class="wf-trend-bar"
                        :class="{ 'is-zero': point.count === 0 }"
                        :style="{ height: point.height + '%' }"
                      ></div>
                    </div>
                    <span class="wf-trend-date">{{ point.label }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="card wf-results-card">
              <div class="card-header wf-card-header wf-table-header">
                <div>
                  <h4 class="card-title mb-1">Daftar Respons</h4>
                  <small class="text-muted">
                    {{ totalRecords }} respons ditemukan
                  </small>
                </div>
                <button
                  type="button"
                  class="btn wf-export-btn"
                  :disabled="exporting || formLoading || !formId"
                  @click="exportResponses"
                >
                  <i
                    :class="
                      exporting
                        ? 'fa fa-spinner fa-spin'
                        : 'fa fa-file-excel-o'
                    "
                    aria-hidden="true"
                  ></i>
                  {{ exporting ? "Menyiapkan Excel..." : "Export Excel" }}
                </button>
              </div>

              <div v-if="exportError" class="alert alert-warning wf-alert" role="alert">
                <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                <span>{{ exportError }}</span>
              </div>

              <div class="wf-filter-row">
                <div class="wf-filter-field">
                  <label for="wf-date-from">Dari tanggal</label>
                  <input
                    id="wf-date-from"
                    v-model="filters.dateFrom"
                    type="date"
                    class="form-control"
                    :disabled="listLoading"
                  />
                </div>
                <div class="wf-filter-field">
                  <label for="wf-date-to">Sampai tanggal</label>
                  <input
                    id="wf-date-to"
                    v-model="filters.dateTo"
                    type="date"
                    class="form-control"
                    :disabled="listLoading"
                  />
                </div>
                <div class="wf-filter-actions">
                  <button
                    type="button"
                    class="btn wf-primary-btn"
                    :disabled="listLoading"
                    @click="applyFilters"
                  >
                    <i class="fa fa-filter" aria-hidden="true"></i>
                    Terapkan
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    :disabled="listLoading || !hasDraftFilter"
                    @click="resetFilters"
                  >
                    Atur Ulang
                  </button>
                </div>
                <small class="wf-filter-note">
                  Filter tanggal hanya berlaku untuk daftar respons. Statistik di
                  atas dihitung dari seluruh data formulir.
                </small>
              </div>

              <div v-if="filterError" class="alert alert-warning wf-alert" role="alert">
                <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                <span>{{ filterError }}</span>
              </div>

              <div v-if="listError" class="wf-table-state">
                <div class="wf-state-icon is-error">
                  <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                </div>
                <h5>Daftar respons belum dapat dimuat</h5>
                <p class="text-muted">{{ listErrorMessage }}</p>
                <button type="button" class="btn wf-primary-btn" @click="loadResponses">
                  <i class="fa fa-refresh" aria-hidden="true"></i>
                  Coba Lagi
                </button>
              </div>

              <div v-else-if="listLoading" class="wf-table-state">
                <i class="fa fa-spinner fa-spin wf-state-icon" aria-hidden="true"></i>
                <p class="text-muted mb-0">Memuat respons...</p>
              </div>

              <div v-else-if="!responses.length" class="wf-table-state">
                <div class="wf-state-icon is-empty">
                  <i class="fa fa-inbox" aria-hidden="true"></i>
                </div>
                <h5>{{ hasActiveFilter ? "Tidak ada respons pada filter ini" : "Belum ada respons" }}</h5>
                <p class="text-muted">
                  {{
                    hasActiveFilter
                      ? "Coba ubah atau atur ulang rentang tanggal."
                      : "Respons akan muncul di sini setelah formulir diisi oleh responden."
                  }}
                </p>
                <button
                  v-if="hasActiveFilter"
                  type="button"
                  class="btn btn-outline-secondary"
                  @click="resetFilters"
                >
                  Atur Ulang Filter
                </button>
              </div>

              <div v-else class="table-responsive">
                <table class="table table-hover custom-table mb-0 wf-table">
                  <thead>
                    <tr>
                      <th>Waktu Pengisian</th>
                      <th>Responden</th>
                      <th>Ringkasan Jawaban</th>
                      <th class="text-right">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in responses" :key="row.id">
                      <td>
                        <span class="wf-date">{{ formatDate(row.create_datetime) }}</span>
                        <span class="wf-response-id" :title="row.id">
                          #{{ shortId(row.id) }}
                        </span>
                      </td>
                      <td>
                        <span class="wf-respondent">{{ respondentText(row) }}</span>
                      </td>
                      <td>
                        <div v-if="row.preview && row.preview.length" class="wf-preview-list">
                          <span
                            v-for="item in row.preview"
                            :key="item.key"
                            class="wf-preview-item"
                          >
                            <strong>{{ item.label || item.key }}</strong>
                            <span>{{ previewText(item) }}</span>
                          </span>
                        </div>
                        <span v-else class="text-muted">
                          {{
                            row.answers_available
                              ? row.schema_snapshot_available
                                ? "Tidak ada jawaban yang dapat diringkas."
                                : "Ringkasan tidak tersedia. Buka detail untuk melihat jawaban."
                              : "Jawaban tidak dapat dibaca."
                          }}
                        </span>
                      </td>
                      <td class="text-right">
                        <button
                          type="button"
                          class="btn btn-sm wf-detail-btn"
                          @click="openDetail(row)"
                        >
                          <i class="fa fa-eye" aria-hidden="true"></i>
                          Lihat Detail
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div v-if="responses.length" class="row align-items-center mt-3 wf-pagination">
                <div class="col-sm-12 col-md-6 mb-2 mb-md-0">
                  <span class="footer__row-count__label">Tampilkan</span>
                  <select
                    v-model.number="perPage"
                    class="select-to-paginasi"
                    style="width: 110px;"
                    :disabled="listLoading"
                    @change="onPerPageChange"
                  >
                    <option :value="10">10 / halaman</option>
                    <option :value="20">20 / halaman</option>
                    <option :value="50">50 / halaman</option>
                    <option :value="100">100 / halaman</option>
                  </select>
                  <span class="footer__row-count__label">
                    dari {{ totalRecords }} respons
                  </span>
                </div>
                <div class="col-sm-12 col-md-6 text-right">
                  <a
                    href="javascript:void(0)"
                    class="btn"
                    :class="{ disabled: currentPage <= 1 || listLoading }"
                    @click.prevent="onPrevChange"
                  >
                    Sebelumnya
                  </a>
                  <span class="wf-page-label">Halaman</span>
                  <input
                    v-model.number="pageInput"
                    type="number"
                    min="1"
                    :max="lastPage"
                    class="wf-page-input"
                    :disabled="listLoading"
                    @keyup.enter="onPageChange(pageInput)"
                    @blur="onPageInputBlur"
                  />
                  <span class="wf-page-label">dari {{ lastPage }}</span>
                  <a
                    href="javascript:void(0)"
                    class="btn"
                    :class="{ disabled: currentPage >= lastPage || listLoading }"
                    @click.prevent="onNextChange"
                  >
                    Berikutnya
                  </a>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <div
      v-if="detailModalOpen"
      class="wf-modal-backdrop"
      @click.self="closeDetail"
    >
      <div
        class="wf-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wf-response-detail-title"
      >
        <div class="wf-modal-header">
          <div>
            <h4 id="wf-response-detail-title" class="mb-1">Detail Respons</h4>
            <small class="text-muted">
              {{ detail ? formatDate(detail.create_datetime) : "Memuat respons..." }}
            </small>
          </div>
          <button
            ref="detailClose"
            type="button"
            class="wf-modal-close"
            aria-label="Tutup detail respons"
            title="Tutup"
            @click="closeDetail"
          >
            <i class="fa fa-times" aria-hidden="true"></i>
          </button>
        </div>

        <div v-if="detailLoading" class="wf-modal-state">
          <i class="fa fa-spinner fa-spin wf-state-icon" aria-hidden="true"></i>
          <p class="text-muted mb-0">Memuat detail respons...</p>
        </div>

        <div v-else-if="detailError" class="wf-modal-state">
          <div class="wf-state-icon is-error">
            <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
          </div>
          <p class="text-muted">{{ detailErrorMessage }}</p>
          <button type="button" class="btn wf-primary-btn" @click="retryDetail">
            <i class="fa fa-refresh" aria-hidden="true"></i>
            Coba Lagi
          </button>
        </div>

        <div v-else-if="detail" class="wf-modal-body">
          <div class="wf-detail-meta">
            <div>
              <span>ID Respons</span>
              <code>{{ detail.id }}</code>
            </div>
            <div>
              <span>Responden</span>
              <strong>{{ respondentText(detail) }}</strong>
            </div>
          </div>

          <div v-if="detailSchemaWarning" class="alert alert-warning wf-alert" role="alert">
            <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
            <span>
              Schema asli respons ini tidak tersedia atau tidak valid. Label field
              ditampilkan berdasarkan key jawaban, bukan schema terbaru.
            </span>
          </div>

          <div v-if="!detail.answers_available" class="alert alert-warning wf-alert" role="alert">
            <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
            <span>Jawaban respons ini tidak dapat dibaca.</span>
          </div>

          <div v-else-if="!detailRows.length" class="wf-inline-empty">
            Tidak ada jawaban pada respons ini.
          </div>

          <div v-else class="wf-answer-list">
            <div
              v-for="row in detailRows"
              :key="row.key"
              class="wf-answer-row"
            >
              <span class="wf-answer-label">{{ row.label }}</span>
              <div
                class="wf-answer-value"
                :class="{ 'is-empty': row.displayValue === 'Tidak diisi' }"
              >
                {{ row.displayValue }}
              </div>
            </div>
          </div>
        </div>

        <div class="wf-modal-footer">
          <button type="button" class="btn btn-outline-secondary" @click="closeDetail">
            Tutup
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LayoutHeader from "@/components/layouts/Header.vue";
import LayoutSidebar from "@/components/layouts/Sidebar.vue";
import { formatLocalDate } from "@/utils/helper";
import {
  extractBackendMessage,
  getHttpStatus,
  getWaajoFormsErrorMessage
} from "@/utils/waajoFormsError";
import {
  buildExportFallbackFilename,
  buildResponseDetailRows,
  formatResponseDateOnly,
  getDownloadFilename,
  getPreviewText,
  getRespondentText,
  getShortResponseId,
  getSnapshotFields
} from "@/utils/waajoFormsResponses";

export default {
  name: "waajo-forms-responses",
  components: {
    LayoutHeader,
    LayoutSidebar
  },
  data() {
    return {
      formId: "",
      formLoading: true,
      form: null,
      pageError: false,
      pageErrorMessage: "",
      sessionExpired: false,
      stats: null,
      statsLoading: false,
      statsError: false,
      statsErrorMessage: "",
      responses: [],
      listLoading: false,
      listError: false,
      listErrorMessage: "",
      exporting: false,
      exportError: "",
      totalRecords: 0,
      totalPages: 0,
      currentPage: 1,
      pageInput: 1,
      perPage: 20,
      filters: {
        dateFrom: "",
        dateTo: ""
      },
      appliedFilters: {
        dateFrom: "",
        dateTo: ""
      },
      filterError: "",
      listRequestId: 0,
      statsRequestId: 0,
      detailModalOpen: false,
      detailResponseId: "",
      detailLoading: false,
      detail: null,
      detailError: false,
      detailErrorMessage: "",
      detailRequestId: 0
    };
  },
  computed: {
    formName() {
      return this.form && this.form.name ? this.form.name : "";
    },
    editRoute() {
      return {
        name: "waajo_forms_editor",
        params: { id: this.formId }
      };
    },
    lastPage() {
      return Math.max(Number(this.totalPages) || 0, 1);
    },
    hasDraftFilter() {
      return (
        String(this.filters.dateFrom || "").trim() !== "" ||
        String(this.filters.dateTo || "").trim() !== ""
      );
    },
    hasActiveFilter() {
      return (
        String(this.appliedFilters.dateFrom || "").trim() !== "" ||
        String(this.appliedFilters.dateTo || "").trim() !== ""
      );
    },
    statCards() {
      const stats = this.stats;
      const unavailable = this.statsLoading || this.statsError || !stats;
      let latestValue = "—";
      if (!unavailable) {
        latestValue = stats.latest_response_at
          ? this.formatDate(stats.latest_response_at)
          : "Belum ada";
      }
      return [
        {
          key: "total",
          label: "Total Respons",
          value: unavailable ? "—" : this.formatCount(stats.total_responses),
          hint: "Seluruh respons formulir ini",
          icon: "fa fa-inbox",
          tone: "brand"
        },
        {
          key: "today",
          label: "Respons Hari Ini",
          value: unavailable ? "—" : this.formatCount(stats.today_responses),
          hint: stats && stats.today_date
            ? "Tanggal " + formatResponseDateOnly(stats.today_date)
            : "Hari ini",
          icon: "fa fa-calendar-check-o",
          tone: "green"
        },
        {
          key: "last7",
          label: "7 Hari Terakhir",
          value: unavailable ? "—" : this.formatCount(stats.last_7_days_responses),
          hint: stats && stats.period_days ? stats.period_days + " hari kalender" : "7 hari kalender",
          icon: "fa fa-line-chart",
          tone: "blue"
        },
        {
          key: "latest",
          label: "Respons Terakhir",
          value: latestValue,
          hint: stats && stats.timezone ? "Zona waktu " + stats.timezone : "",
          icon: "fa fa-clock-o",
          tone: "violet"
        }
      ];
    },
    dailyTrend() {
      return this.stats && Array.isArray(this.stats.daily_trend)
        ? this.stats.daily_trend
        : [];
    },
    trendBars() {
      let max = 0;
      this.dailyTrend.forEach(function(point) {
        max = Math.max(max, Number(point.count) || 0);
      });
      max = Math.max(max, 1);

      return this.dailyTrend.map(function(point) {
        const count = Number(point.count) || 0;
        const date = String(point.date || "");
        const label = date.length === 10 ? date.slice(8, 10) + "/" + date.slice(5, 7) : date;
        return {
          date: date,
          label: label,
          count: count,
          height: count > 0 ? Math.max(8, Math.round((count / max) * 100)) : 0,
          title: date + ": " + count + " respons"
        };
      });
    },
    trendHasData() {
      return this.dailyTrend.some(function(point) {
        return (Number(point.count) || 0) > 0;
      });
    },
    trendAriaLabel() {
      return this.trendBars
        .map(function(point) {
          return point.date + ": " + point.count + " respons";
        })
        .join(", ");
    },
    detailSchema() {
      return getSnapshotFields(this.detail);
    },
    detailSchemaWarning() {
      return !!this.detail && !this.detailSchema.available;
    },
    detailRows() {
      return buildResponseDetailRows(this.detail);
    }
  },
  watch: {
    "$route.params.id"(value) {
      const nextId = String(value || "").trim();
      if (nextId !== this.formId) {
        this.formId = nextId;
        this.loadPage();
      }
    }
  },
  mounted() {
    this.formId = String(this.$route.params.id || "").trim();
    document.addEventListener("keydown", this.onDocumentKeydown);
    this.loadPage();
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onDocumentKeydown);
  },
  methods: {
    async loadPage() {
      this.pageError = false;
      this.pageErrorMessage = "";
      this.form = null;
      this.stats = null;
      this.responses = [];
      this.totalRecords = 0;
      this.totalPages = 0;
      this.currentPage = 1;
      this.pageInput = 1;
      this.appliedFilters = {
        dateFrom: String(this.filters.dateFrom || "").trim(),
        dateTo: String(this.filters.dateTo || "").trim()
      };
      this.filterError = "";
      this.exportError = "";
      this.closeDetail();

      if (!this.formId) {
        this.formLoading = false;
        this.pageError = true;
        this.pageErrorMessage = "ID formulir tidak valid.";
        return;
      }

      await this.loadForm();
      if (this.pageError || this.sessionExpired) {
        return;
      }

      await Promise.all([this.loadStats(), this.loadResponses()]);
    },
    async loadForm() {
      this.formLoading = true;
      try {
        const response = await this.$store.dispatch(
          "waajoForms/detailWaajoForm",
          { id: this.formId }
        );
        this.form = response && response.data ? response.data : null;
        if (!this.form || !this.form.id) {
          this.pageError = true;
          this.pageErrorMessage = "Form tidak ditemukan atau sudah dihapus.";
        }
      } catch (error) {
        if (getHttpStatus(error) === 401) {
          this.handleSessionExpired();
          return;
        }
        this.pageError = true;
        this.pageErrorMessage = this.getErrorMessage(
          error,
          "Form belum dapat dimuat. Coba lagi."
        );
      } finally {
        this.formLoading = false;
      }
    },
    async loadStats() {
      const requestId = ++this.statsRequestId;
      this.statsLoading = true;
      this.statsError = false;
      this.statsErrorMessage = "";
      try {
        const response = await this.$store.dispatch(
          "waajoForms/statsWaajoFormResponses",
          { formId: this.formId }
        );
        if (requestId !== this.statsRequestId) {
          return;
        }
        this.stats = response && response.data ? response.data : null;
      } catch (error) {
        if (requestId !== this.statsRequestId) {
          return;
        }
        if (getHttpStatus(error) === 401) {
          this.handleSessionExpired();
          return;
        }
        this.statsError = true;
        this.statsErrorMessage = this.getErrorMessage(
          error,
          "Statistik respons belum dapat dimuat."
        );
      } finally {
        if (requestId === this.statsRequestId) {
          this.statsLoading = false;
        }
      }
    },
    async loadResponses() {
      const requestId = ++this.listRequestId;
      this.listLoading = true;
      this.listError = false;
      this.listErrorMessage = "";

      try {
        const params = {
          form_id: this.formId,
          page: this.currentPage,
          per_page: this.perPage
        };
        const from = String(this.appliedFilters.dateFrom || "").trim();
        const to = String(this.appliedFilters.dateTo || "").trim();
        if (from) {
          params.date_from = from;
        }
        if (to) {
          params.date_to = to;
        }

        const response = await this.$store.dispatch(
          "waajoForms/listWaajoFormResponses",
          params
        );
        if (requestId !== this.listRequestId) {
          return;
        }

        const payload = response && response.data ? response.data : {};
        this.responses = Array.isArray(payload.data) ? payload.data : [];
        this.totalRecords = Number(payload.totalRecords) || 0;
        this.totalPages = Number(payload.totalPages) || 0;

        const responsePage = Number(payload.page);
        if (responsePage > 0) {
          this.currentPage = responsePage;
        }
        const responsePerPage = Number(payload.perPage);
        if (responsePerPage > 0) {
          this.perPage = responsePerPage;
        }
        this.pageInput = this.currentPage;
      } catch (error) {
        if (requestId !== this.listRequestId) {
          return;
        }
        if (getHttpStatus(error) === 401) {
          this.handleSessionExpired();
          return;
        }
        this.listError = true;
        this.listErrorMessage = this.getErrorMessage(
          error,
          "Daftar respons belum dapat dimuat. Coba lagi."
        );
      } finally {
        if (requestId === this.listRequestId) {
          this.listLoading = false;
        }
      }
    },
    applyFilters() {
      const from = String(this.filters.dateFrom || "").trim();
      const to = String(this.filters.dateTo || "").trim();
      this.filterError = "";
      if (from && to && from > to) {
        this.filterError = "Tanggal awal tidak boleh lebih besar dari tanggal akhir.";
        return;
      }
      this.appliedFilters = { dateFrom: from, dateTo: to };
      this.currentPage = 1;
      this.pageInput = 1;
      this.loadResponses();
    },
    resetFilters() {
      this.filters = { dateFrom: "", dateTo: "" };
      this.appliedFilters = { dateFrom: "", dateTo: "" };
      this.filterError = "";
      this.currentPage = 1;
      this.pageInput = 1;
      this.loadResponses();
    },
    onPerPageChange() {
      this.currentPage = 1;
      this.pageInput = 1;
      this.loadResponses();
    },
    onPrevChange() {
      if (this.currentPage <= 1 || this.listLoading) {
        return;
      }
      this.onPageChange(this.currentPage - 1);
    },
    onNextChange() {
      if (this.currentPage >= this.lastPage || this.listLoading) {
        return;
      }
      this.onPageChange(this.currentPage + 1);
    },
    onPageChange(value) {
      const page = Math.min(
        Math.max(Number(value) || 1, 1),
        this.lastPage
      );
      this.currentPage = page;
      this.pageInput = page;
      this.loadResponses();
    },
    onPageInputBlur() {
      this.pageInput = this.currentPage;
    },
    async exportResponses() {
      if (this.exporting || !this.formId) {
        return;
      }
      this.exporting = true;
      this.exportError = "";
      try {
        const response = await this.$store.dispatch(
          "waajoForms/exportWaajoFormResponses",
          { formId: this.formId }
        );
        const data = response && response.data ? response.data : null;
        if (!data || !data.size) {
          throw new Error("File Excel kosong.");
        }
        const headers = (response && response.headers) || {};
        const disposition =
          headers["content-disposition"] || headers["Content-Disposition"] || "";
        const fallbackName = buildExportFallbackFilename(this.formName);
        const filename = getDownloadFilename(disposition, fallbackName);
        const mimeType =
          headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        if (String(mimeType).toLowerCase().indexOf("application/json") !== -1) {
          this.exportError = "File Excel tidak tersedia. Coba lagi.";
          return;
        }
        const blob =
          typeof Blob !== "undefined" && data instanceof Blob
            ? data
            : new Blob([data], { type: mimeType });
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = filename;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.setTimeout(function() {
          window.URL.revokeObjectURL(objectUrl);
        }, 1000);
      } catch (error) {
        if (getHttpStatus(error) === 401) {
          this.handleSessionExpired();
          return;
        }
        this.exportError = this.getErrorMessage(
          error,
          "File Excel belum dapat diunduh. Coba lagi."
        );
      } finally {
        this.exporting = false;
      }
    },
    async openDetail(row) {
      if (!row || !row.id) {
        return;
      }
      const requestId = ++this.detailRequestId;
      this.detailModalOpen = true;
      this.detailResponseId = row.id;
      this.detailLoading = true;
      this.detail = null;
      this.detailError = false;
      this.detailErrorMessage = "";

      this.$nextTick(() => {
        if (this.$refs.detailClose) {
          this.$refs.detailClose.focus();
        }
      });

      try {
        const response = await this.$store.dispatch(
          "waajoForms/detailWaajoFormResponse",
          { id: row.id }
        );
        if (requestId !== this.detailRequestId) {
          return;
        }
        this.detail = response && response.data ? response.data : null;
        if (!this.detail || !this.detail.id) {
          this.detailError = true;
          this.detailErrorMessage = "Detail respons tidak ditemukan.";
        }
      } catch (error) {
        if (requestId !== this.detailRequestId) {
          return;
        }
        if (getHttpStatus(error) === 401) {
          this.closeDetail();
          this.handleSessionExpired();
          return;
        }
        if (getHttpStatus(error) === 404) {
          this.closeDetail();
          this.$fire({
            title: "Respons tidak ditemukan",
            text: "Respons ini tidak ada atau tidak dapat diakses.",
            type: "warning",
            timer: 3200
          });
          return;
        }
        this.detailError = true;
        this.detailErrorMessage = this.getErrorMessage(
          error,
          "Detail respons belum dapat dimuat."
        );
      } finally {
        if (requestId === this.detailRequestId) {
          this.detailLoading = false;
        }
      }
    },
    retryDetail() {
      if (this.detailResponseId) {
        this.openDetail({ id: this.detailResponseId });
      }
    },
    closeDetail() {
      this.detailRequestId += 1;
      this.detailModalOpen = false;
      this.detailResponseId = "";
      this.detailLoading = false;
      this.detail = null;
      this.detailError = false;
      this.detailErrorMessage = "";
    },
    onDocumentKeydown(event) {
      if (event.key === "Escape" && this.detailModalOpen) {
        this.closeDetail();
      }
    },
    formatDate(value) {
      const unix = Number(value);
      if (!unix) {
        return "Belum ada";
      }
      return formatLocalDate(unix);
    },
    formatCount(value) {
      const number = Number(value);
      if (!isFinite(number)) {
        return "—";
      }
      return number.toLocaleString("id-ID");
    },
    respondentText(item) {
      return getRespondentText(item);
    },
    previewText(item) {
      return getPreviewText(item);
    },
    shortId(id) {
      return getShortResponseId(id);
    },
    getErrorMessage(error, fallback) {
      if (getHttpStatus(error) === 401) {
        this.handleSessionExpired();
      }
      if (getHttpStatus(error) === 406) {
        return extractBackendMessage(error) || fallback;
      }
      return getWaajoFormsErrorMessage(error, fallback);
    },
    handleSessionExpired() {
      if (this.sessionExpired) {
        return;
      }
      this.sessionExpired = true;
      const currentPath =
        this.$route && this.$route.fullPath
          ? this.$route.fullPath
          : "/forms";
      this.$store.dispatch("login/resetToken").finally(() => {
        this.$router
          .replace({
            path: "/login",
            query: { redirect: currentPath }
          })
          .catch(() => {});
      });
    }
  }
};
</script>

<style scoped>
.wf-results-page {
  padding-bottom: 32px;
}

.wf-primary-btn {
  background-color: #fe9b44;
  border-color: #fe9b44;
  color: #ffffff;
}

.wf-primary-btn:hover,
.wf-primary-btn:focus {
  background-color: #f08a2e;
  border-color: #f08a2e;
  color: #ffffff;
}

.wf-primary-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.wf-results-actions .btn {
  min-width: 140px;
}

.wf-results-card {
  margin-bottom: 22px;
  border: 1px solid #e8edf3;
  border-radius: 14px;
  box-shadow: 0 6px 22px rgba(15, 23, 42, 0.04);
}

.wf-state-card {
  padding: 52px 24px;
  text-align: center;
}

.wf-state-icon {
  width: 48px;
  height: 48px;
  margin: 0 auto 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #fff3e6;
  color: #b45309;
  font-size: 20px;
}

.wf-state-icon.is-error {
  background: #fef2f2;
  color: #b91c1c;
}

.wf-state-icon.is-empty {
  background: #f1f5f9;
  color: #64748b;
}

.wf-state-card h5,
.wf-table-state h5 {
  margin-bottom: 6px;
  color: #1e293b;
  font-weight: 700;
}

.wf-state-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
}

.wf-stat-row {
  margin-bottom: 22px;
}

.wf-stat-card {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 13px;
  height: 100%;
  margin-bottom: 0;
  padding: 18px;
  border: 1px solid #e8edf3;
  border-radius: 14px;
  box-shadow: 0 4px 18px rgba(15, 23, 42, 0.035);
}

.wf-stat-icon {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  font-size: 17px;
}

.wf-stat-icon.is-brand {
  background: #fff3e6;
  color: #d97706;
}

.wf-stat-icon.is-green {
  background: #e8f8ee;
  color: #18753c;
}

.wf-stat-icon.is-blue {
  background: #eaf2ff;
  color: #2563eb;
}

.wf-stat-icon.is-violet {
  background: #f3edff;
  color: #7c3aed;
}

.wf-stat-copy {
  min-width: 0;
}

.wf-stat-label {
  display: block;
  margin-bottom: 3px;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
}

.wf-stat-value {
  display: block;
  color: #1e293b;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.wf-stat-copy small {
  display: block;
  margin-top: 4px;
  color: #94a3b8;
  font-size: 11px;
  line-height: 1.4;
}

.wf-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid #eef2f7;
}

.wf-card-header .card-title {
  color: #1e293b;
  font-size: 16px;
  font-weight: 700;
}

.wf-table-header {
  align-items: center;
}

.wf-export-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #f6c892;
  border-radius: 8px;
  background: #fff8ef;
  color: #b45309;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.wf-export-btn:hover,
.wf-export-btn:focus {
  border-color: #fe9b44;
  background: #fe9b44;
  color: #ffffff;
}

.wf-export-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.wf-timezone-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 9px;
  border-radius: 999px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 11px;
  white-space: nowrap;
}

.wf-inline-loading,
.wf-inline-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 120px;
  color: #94a3b8;
  font-size: 13px;
}

.wf-trend-chart {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;
  min-height: 190px;
  padding: 12px 4px 0;
}

.wf-trend-column {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 170px;
}

.wf-trend-count {
  margin-bottom: 5px;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
}

.wf-trend-track {
  width: 100%;
  max-width: 40px;
  height: 130px;
  display: flex;
  align-items: flex-end;
  overflow: hidden;
  border-radius: 7px;
  background: #f8fafc;
}

.wf-trend-bar {
  width: 100%;
  border-radius: 7px 7px 4px 4px;
  background: linear-gradient(180deg, #ffb86b 0%, #fe9b44 100%);
  transition: height 0.25s ease;
}

.wf-trend-bar.is-zero {
  background: #eef2f7;
}

.wf-trend-date {
  margin-top: 7px;
  color: #94a3b8;
  font-size: 10px;
  white-space: nowrap;
}

.wf-filter-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  padding: 15px 20px;
  border-bottom: 1px solid #eef2f7;
  background: #fbfcfe;
}

.wf-filter-field {
  min-width: 165px;
}

.wf-filter-field label {
  display: block;
  margin-bottom: 5px;
  color: #64748b;
  font-size: 11px;
  font-weight: 600;
}

.wf-filter-field .form-control {
  height: 38px;
  border-color: #e2e8f0;
  border-radius: 8px;
}

.wf-filter-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.wf-filter-note {
  flex: 1 1 210px;
  color: #94a3b8;
  font-size: 11px;
  line-height: 1.5;
}

.wf-alert {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 14px 20px;
  border-radius: 9px;
  font-size: 13px;
}

.wf-table-state {
  padding: 42px 22px;
  text-align: center;
}

.wf-table-state p {
  max-width: 520px;
  margin: 0 auto 14px;
}

.wf-table thead th {
  border-top: 0;
  background: #fbfcfe;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  white-space: nowrap;
}

.wf-table tbody td {
  vertical-align: middle;
  border-color: #eef2f7;
  color: #334155;
  font-size: 13px;
}

.wf-date {
  display: block;
  color: #334155;
  font-size: 12px;
  white-space: nowrap;
}

.wf-response-id {
  display: block;
  margin-top: 3px;
  color: #94a3b8;
  font-family: monospace;
  font-size: 10px;
}

.wf-respondent {
  display: inline-block;
  max-width: 230px;
  color: #334155;
  overflow-wrap: anywhere;
}

.wf-preview-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: 430px;
}

.wf-preview-item {
  display: block;
  color: #64748b;
  font-size: 11px;
  line-height: 1.45;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wf-preview-item strong {
  margin-right: 4px;
  color: #475569;
  font-weight: 600;
}

.wf-detail-btn {
  border: 1px solid #f6c892;
  border-radius: 8px;
  background: #fff8ef;
  color: #b45309;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.wf-detail-btn:hover,
.wf-detail-btn:focus {
  background: #fe9b44;
  border-color: #fe9b44;
  color: #ffffff;
}

.wf-pagination {
  padding: 0 16px 14px;
}

.wf-page-label {
  margin: 0 6px;
  color: #64748b;
  font-size: 12px;
}

.wf-page-input {
  width: 48px;
  height: 30px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  text-align: center;
  color: #334155;
  font-size: 12px;
}

.wf-modal-backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: rgba(15, 23, 42, 0.48);
}

.wf-modal-card {
  width: 100%;
  max-width: 680px;
  max-height: calc(100vh - 36px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.25);
}

.wf-modal-header,
.wf-modal-footer {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
}

.wf-modal-header {
  border-bottom: 1px solid #eef2f7;
}

.wf-modal-header h4 {
  color: #1e293b;
  font-size: 17px;
  font-weight: 700;
}

.wf-modal-footer {
  justify-content: flex-end;
  border-top: 1px solid #eef2f7;
}

.wf-modal-close {
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background: #f8fafc;
  color: #64748b;
  cursor: pointer;
}

.wf-modal-close:hover,
.wf-modal-close:focus {
  background: #fff3e6;
  color: #b45309;
  outline: none;
}

.wf-modal-state {
  padding: 46px 24px;
  text-align: center;
}

.wf-modal-body {
  padding: 18px 20px 20px;
  overflow-y: auto;
}

.wf-detail-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
  padding: 13px 14px;
  border-radius: 10px;
  background: #fbfcfe;
  border: 1px solid #eef2f7;
}

.wf-detail-meta span {
  display: block;
  margin-bottom: 3px;
  color: #94a3b8;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.wf-detail-meta strong,
.wf-detail-meta code {
  color: #334155;
  font-size: 12px;
  overflow-wrap: anywhere;
}

.wf-detail-meta code {
  font-size: 10px;
}

.wf-answer-list {
  border: 1px solid #eef2f7;
  border-radius: 10px;
  overflow: hidden;
}

.wf-answer-row {
  display: grid;
  grid-template-columns: minmax(140px, 36%) minmax(0, 1fr);
  gap: 14px;
  padding: 12px 14px;
  border-bottom: 1px solid #eef2f7;
}

.wf-answer-row:last-child {
  border-bottom: 0;
}

.wf-answer-label {
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.wf-answer-value {
  color: #1e293b;
  font-size: 13px;
  line-height: 1.55;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.wf-answer-value.is-empty {
  color: #94a3b8;
  font-style: italic;
}

@media (max-width: 767.98px) {
  .wf-results-actions {
    width: 100%;
    margin-top: 10px;
  }

  .wf-results-actions .btn {
    width: 100%;
  }

  .wf-filter-field {
    flex: 1 1 100%;
    min-width: 0;
  }

  .wf-filter-actions {
    width: 100%;
  }

  .wf-filter-actions .btn {
    flex: 1 1 auto;
  }

  .wf-table-header {
    flex-wrap: wrap;
  }

  .wf-export-btn {
    width: 100%;
    justify-content: center;
    margin-top: 8px;
  }

  .wf-trend-chart {
    gap: 5px;
  }

  .wf-trend-track {
    max-width: 28px;
  }

  .wf-detail-meta,
  .wf-answer-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }

  .wf-modal-backdrop {
    padding: 10px;
  }

  .wf-modal-card {
    max-height: calc(100vh - 20px);
  }
}
</style>



// WEBPACK FOOTER //
// src/views/forms/responses.vue